import React from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Box, CalendarDays, ChevronRight, FileText, MapPin, Truck } from "lucide-react";
import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Text,
} from "@musa.tech/design-system";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { maskEstablishmentName, maskAddress } from "@/utils/demoMode";
import { removeGeneratorPrefix } from "@/utils/establishmentUtils";
import { getMaterialDisplayLabel } from "@/utils/materialSystem";
import {
  coletaStatusBadgeVariant,
  coletaStatusLabel,
  formatFullAddress,
  parseCollectDate,
} from "@/utils/coletasCalendar";
import type { CollectForTable } from "@/hooks/useCollectsApiData";

interface ColetaDetalheDrawerProps {
  collect: CollectForTable | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenMtr?: (collectId: string) => void;
}

const Field = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-start gap-3 py-3">
    <span className="mt-0.5 text-neutral-400">{icon}</span>
    <div className="min-w-0">
      <Text variant="label" size="small" className="block text-neutral-500">
        {label}
      </Text>
      <div className="mt-0.5">{children}</div>
    </div>
  </div>
);

/** Painel lateral de detalhes de uma coleta recorrente (Próximas Coletas). */
export const ColetaDetalheDrawer = ({
  collect,
  open,
  onOpenChange,
  onOpenMtr,
}: ColetaDetalheDrawerProps) => {
  const { isDemoMode } = useDemoMode();
  const { trackButtonClick } = useAnalytics();

  const rawName =
    collect?.establishments?.establishment_name ||
    collect?.establishment_name ||
    "";
  const name = maskEstablishmentName(
    removeGeneratorPrefix(rawName, isDemoMode),
    isDemoMode,
  );
  const material = getMaterialDisplayLabel(collect?.kind_of_material || "");
  const packaging = collect?.packaging_name ?? null;
  const rawAddressLines = formatFullAddress({
    street: collect?.street,
    number: collect?.number,
    neighborhood: collect?.neighborhood,
    city: collect?.city,
    state: collect?.state,
  });
  const addressLines = isDemoMode && rawAddressLines.length > 0
    ? [maskAddress(rawAddressLines[0], isDemoMode)]
    : rawAddressLines;
  const date = parseCollectDate(collect?.planned_date);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[95vw] sm:max-w-md bg-white">
          <SheetHeader className="border-b border-neutral-200 pb-3">
            <div className="flex items-center justify-between gap-3 pr-6">
              <SheetTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-neutral-700" aria-hidden />
                <Text variant="heading" size="large" className="text-neutral-900">
                  Detalhes da Coleta
                </Text>
              </SheetTitle>
              {collect && (
                <Badge
                  variant={coletaStatusBadgeVariant(collect.sla_status)}
                  className="whitespace-nowrap shrink-0"
                >
                  {coletaStatusLabel(collect.sla_status)}
                </Badge>
              )}
            </div>
          </SheetHeader>

          {collect && (
            <div className="mt-4">
              <div className="flex items-center gap-2">
                <Truck className="h-5 w-5 shrink-0 text-neutral-700" aria-hidden />
                <Text variant="heading" size="small" className="text-neutral-900 break-words">
                  {name || "—"}
                </Text>
              </div>

              <div className="mt-4 divide-y divide-neutral-100">
                <Field
                  icon={<CalendarDays className="h-4 w-4" aria-hidden />}
                  label="Data Agendada"
                >
                  <Text variant="body" className="text-neutral-900">
                    {date
                      ? capitalize(
                          format(date, "EEEE, dd 'de' MMMM 'de' yyyy", {
                            locale: ptBR,
                          }),
                        )
                      : "—"}
                  </Text>
                </Field>

                <Field
                  icon={<Box className="h-4 w-4" aria-hidden />}
                  label="Tipo de Resíduo"
                >
                  {material ? (
                    <Badge
                      variant="info"
                      iconLeft={<Box className="h-3 w-3 shrink-0" aria-hidden />}
                      className="max-w-full overflow-hidden [&>p]:min-w-0 [&>p]:truncate"
                      title={material}
                    >
                      {material}
                    </Badge>
                  ) : (
                    <Text variant="body" className="text-neutral-900">
                      —
                    </Text>
                  )}
                </Field>

                <Field
                  icon={<Box className="h-4 w-4" aria-hidden />}
                  label="Acondicionamento"
                >
                  {packaging ? (
                    <Badge
                      variant="secondary"
                      className="max-w-full overflow-hidden [&>p]:min-w-0 [&>p]:truncate"
                      title={packaging}
                    >
                      {packaging}
                    </Badge>
                  ) : (
                    <Text variant="body" className="text-neutral-900">—</Text>
                  )}
                </Field>

                <Field
                  icon={<MapPin className="h-4 w-4" aria-hidden />}
                  label="Endereço"
                >
                  {addressLines.length > 0 ? (
                    <div>
                      {addressLines.map((line, i) => (
                        <Text key={i} variant="body" className="block text-neutral-900">
                          {line}
                        </Text>
                      ))}
                    </div>
                  ) : (
                    <Text variant="body" className="text-neutral-900">—</Text>
                  )}
                </Field>

                <Field
                  icon={<Truck className="h-4 w-4" aria-hidden />}
                  label="Tipo de Coleta"
                >
                  <Text variant="body" className="text-neutral-900">
                    Recorrente
                  </Text>
                </Field>

              </div>

              {collect.has_mtr && (
                <Button
                  type="button"
                  onClick={() => {
                    trackButtonClick({
                      buttonLabel: "proximas_coletas_visualizar_mtr",
                      collection_id: collect.collect_id,
                    });
                    onOpenMtr?.(collect.collect_id);
                  }}
                  aria-label="Abrir Manifesto de Transporte de Resíduos (MTR)"
                  className="mt-4 flex h-auto w-full items-center justify-start gap-4 rounded-xl bg-primary-600 px-4 py-4 text-left shadow-sm transition-all hover:bg-primary-700 hover:shadow-md active:bg-primary-800"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20">
                    <FileText className="h-5 w-5 text-white" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Text variant="label" size="small" className="block font-semibold text-white">
                      Manifesto de Transporte (MTR)
                    </Text>
                    <Text variant="body" size="small" className="mt-0.5 block text-primary-200">
                      Toque para visualizar o documento
                    </Text>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-white/70" aria-hidden />
                </Button>
              )}

              <div className="mt-4 flex justify-end">
                <Button variant="secondary" onClick={() => onOpenChange(false)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
    </Sheet>
  );
};

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
